/**
 * §P.2 · Quién da este grupo.
 *
 * **Al entrenador no se le enseña esta sección.** `BFF_BO.md` §7.5: «el entrenador
 * ve los matriculados —es a quién da clase— y **no ve los profesores del grupo**,
 * que es organización del club». Y no es solo cortesía: `GET /coaches` responde
 * `403` a un entrenador, así que enseñársela sería enseñar una pantalla que falla.
 *
 * Lo que la pantalla pone de su parte, en `reglasDeProfesores.ts`:
 *
 * - **Un solo principal**, degradando al anterior al marcar a otro, en vez de
 *   mandar la combinación que da `422 two_head_coaches`.
 * - **Y nunca una lista sin principal.** La regla del servidor es «no más de
 *   uno», no «al menos uno», así que quitar al principal y dejar dos ayudantes
 *   pasaría la validación y dejaría un grupo del que no responde nadie.
 * - **Solo miembros activos del club**, elegidos de la lista del personal. No hay
 *   un campo de correo libre: la clave foránea apunta a `users` y la base
 *   aceptaría a una madre de otro club.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import {
  anadir,
  cuantosPrincipales,
  hacerPrincipal,
  puedenDarClase,
  quitar,
  MAXIMO_DE_PROFESORES,
  type Asignacion,
  type DelPersonal,
  type Profesor,
} from '@/club/grupos/reglasDeProfesores';

const nombreDe = (p: { name: string | null; email: string | null }): string =>
  p.name || p.email || 'Sin nombre';

export function ProfesoresDelGrupo({
  club,
  grupoId,
  puedeEditar,
}: {
  club: string;
  grupoId: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [borrador, setBorrador] = useState<readonly Asignacion[] | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const profesores = useQuery({
    queryKey: ['profesores', club, grupoId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .groups({ groupId: grupoId })
        .coaches.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Profesor[];
    },
  });

  // El personal del club. No incluye tutores a propósito: «los tutores no salen
  // aquí», y un tutor no da clase.
  const personal = useQuery({
    queryKey: ['personal', club],
    enabled: borrador !== null,
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).memberships.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DelPersonal[];
    },
  });

  const guardar = useMutation({
    mutationFn: async (lista: readonly Asignacion[]) => {
      const r = await api.v1
        .clubs({ club })
        .groups({ groupId: grupoId })
        .coaches.put({
          // La lista entera, como el horario.
          coaches: lista.map((x) => ({ user_id: x.user_id, role: x.role })),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setProblema(null);
      setBorrador(null);
      void cache.invalidateQueries({ queryKey: ['profesores', club, grupoId] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (profesores.isPending)
    return <Cargando filas={2} etiqueta="Cargando los profesores" />;

  if (profesores.isError)
    return (
      <PanelError
        problema={aProblema(profesores.error)}
        reintentar={() => void profesores.refetch()}
      />
    );

  // ── Edición: la lista completa ───────────────────────────────────────────
  if (borrador) {
    const candidatos = puedenDarClase(personal.data ?? []).filter(
      (p) => !borrador.some((a) => a.user_id === p.user_id)
    );

    const nombrePorId = (id: string): string => {
      const enPersonal = (personal.data ?? []).find((p) => p.user_id === id);
      if (enPersonal) return nombreDe(enPersonal);
      const yaAsignado = profesores.data.find((p) => p.user_id === id);
      return yaAsignado ? nombreDe(yaAsignado) : id;
    };

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate(borrador);
        }}
      >
        <p className="tenue">
          El <strong>principal</strong> es quien responde por el grupo. Solo
          puede haber uno: al marcar a otro, el anterior pasa a ayudante.
        </p>

        {borrador.length === 0 ? (
          <p className="tenue">
            Nadie asignado. Un grupo sin profesor es válido —todavía no se ha
            decidido quién lo da— pero conviene decidirlo antes de que empiecen
            las clases.
          </p>
        ) : (
          <ul className="profesores">
            {borrador.map((asig) => (
              <li key={asig.user_id}>
                <strong>{nombrePorId(asig.user_id)}</strong>

                <label className="opcion opcion-fila">
                  <input
                    type="radio"
                    name="principal"
                    checked={asig.role === 'principal'}
                    onChange={() =>
                      setBorrador(hacerPrincipal(borrador, asig.user_id))
                    }
                  />
                  <span>Principal</span>
                </label>

                <span className="tenue">
                  {asig.role === 'ayudante' ? 'Ayudante' : ''}
                </span>

                <button
                  type="button"
                  className="btn-plano"
                  aria-label={`Quitar a ${nombrePorId(asig.user_id)}`}
                  onClick={() => setBorrador(quitar(borrador, asig.user_id))}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {personal.isPending && (
          <Cargando filas={1} etiqueta="Cargando el personal" />
        )}

        {borrador.length < MAXIMO_DE_PROFESORES && candidatos.length > 0 && (
          <div className="buscador">
            <p className="tenue">Añadir del personal del club:</p>
            {candidatos.map((p) => (
              <button
                key={p.user_id}
                type="button"
                className="btn-plano candidato"
                onClick={() => setBorrador(anadir(borrador, p.user_id))}
              >
                {nombreDe(p)} · {p.role === 'coach' ? 'entrenador' : p.role}
              </button>
            ))}
          </div>
        )}

        {personal.data && candidatos.length === 0 && borrador.length > 0 && (
          <p className="tenue">
            Todo el personal que puede dar clase ya está en este grupo.
          </p>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={guardar.isPending}>
            {guardar.isPending
              ? 'Guardando…'
              : borrador.length === 0
                ? 'Guardar sin profesores'
                : `Guardar ${borrador.length} profesor${borrador.length === 1 ? '' : 'es'}`}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={guardar.isPending}
            onClick={() => {
              setBorrador(null);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── Lectura ──────────────────────────────────────────────────────────────
  if (profesores.data.length === 0) {
    return (
      <Vacio
        titulo="Nadie asignado a este grupo"
        explicacion="El principal es quien responde por el grupo. Conviene decidirlo antes de que empiecen las clases: es quien aparece en la clase del día."
        {...(puedeEditar
          ? {
              accion: (
                <button type="button" onClick={() => setBorrador([])}>
                  Asignar profesores
                </button>
              ),
            }
          : {})}
      />
    );
  }

  return (
    <>
      <ul className="profesores">
        {profesores.data.map((p) => (
          <li key={p.user_id}>
            <strong>{nombreDe(p)}</strong>
            {p.role === 'principal' ? (
              <span className="chapa">Principal</span>
            ) : (
              <span className="tenue">Ayudante</span>
            )}
            {p.email && <span className="tenue">{p.email}</span>}
          </li>
        ))}
      </ul>

      {/* Si el servidor devolviera una lista sin principal, se dice. */}
      {cuantosPrincipales(
        profesores.data.map((p) => ({
          user_id: p.user_id,
          role: p.role as 'principal' | 'ayudante',
        }))
      ) === 0 && (
        <div className="nota">
          <strong>Este grupo no tiene principal.</strong> Nadie responde por él:
          si hay una incidencia en clase, no está claro a quién se le pregunta.
        </div>
      )}

      {puedeEditar && (
        <div className="acciones">
          <button
            type="button"
            className="btn-plano"
            onClick={() =>
              setBorrador(
                profesores.data.map((p) => ({
                  user_id: p.user_id,
                  role: (p.role === 'principal' ? 'principal' : 'ayudante') as
                    'principal' | 'ayudante',
                }))
              )
            }
          >
            Cambiar los profesores
          </button>
        </div>
      )}
    </>
  );
}
